import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Copy, Key, Plus, Prohibit } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";

const fmtDate = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") : "-");

export function ApiTokensSection() {
  const { toast } = useToast();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [tokenName, setTokenName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const fetchTokens = useCallback(() => {
    api
      .get("/api-tokens")
      .then(({ data }) => setTokens(data.tokens))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(fetchTokens, [fetchTokens]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!tokenName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/api-tokens", { name: tokenName.trim() });
      setNewToken(data.token);
      setTokenName("");
      fetchTokens();
    } catch (err) {
      toast({
        title: "Could not create token",
        description: formatApiErrorDetail(err.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newToken);
      toast({ title: "Token copied" });
    } catch {
      toast({ title: "Copy failed - select and copy manually", variant: "destructive" });
    }
  };

  const handleRevoke = async () => {
    try {
      await api.delete(`/api-tokens/${revokeTarget.id}`);
      toast({ title: "Token revoked" });
      fetchTokens();
    } catch (err) {
      toast({
        title: "Could not revoke token",
        description: formatApiErrorDetail(err.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setRevokeTarget(null);
    }
  };

  const closeCreate = (open) => {
    setCreateOpen(open);
    if (!open) setNewToken(null);
  };

  return (
    <div className="mt-8" data-testid="api-tokens-section">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
            <Key size={16} /> API Tokens
          </h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Read-only tokens for external systems. Send as: Authorization: Bearer &lt;token&gt; (GET /api/fm/... only)
          </p>
        </div>
        <Button
          size="sm"
          className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
          onClick={() => setCreateOpen(true)}
          data-testid="create-api-token-button"
        >
          <Plus size={14} className="mr-1.5" /> New Token
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading...</div>
      ) : tokens.length === 0 ? (
        <div className="rounded-sm border border-dashed border-zinc-300 p-4 text-center text-xs text-zinc-500" data-testid="api-tokens-empty">
          No API tokens yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table className="min-w-[600px]" data-testid="api-tokens-table">
            <TableHeader>
              <TableRow className="border-b border-zinc-300">
                <TableHead className="text-xs uppercase tracking-[0.05em]">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em]">Token</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em]">Created</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em]">Last Used</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em]">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((t) => (
                <TableRow key={t.id} className="border-b border-zinc-200">
                  <TableCell className="text-sm font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono-data text-xs text-zinc-500">{t.token_prefix}</TableCell>
                  <TableCell className="text-xs text-zinc-500">{fmtDate(t.created_at)}</TableCell>
                  <TableCell className="text-xs text-zinc-500">{fmtDate(t.last_used_at)}</TableCell>
                  <TableCell>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        t.revoked ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                      }`}
                    >
                      {t.revoked ? "Revoked" : "Active"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {!t.revoked && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => setRevokeTarget(t)}
                        data-testid={`revoke-api-token-button-${t.id}`}
                      >
                        <Prohibit size={14} className="mr-1.5" /> Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={closeCreate}>
        <DialogContent className="rounded-sm" data-testid="create-api-token-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New API Token</DialogTitle>
            <DialogDescription>
              {newToken
                ? "Copy this token now - it will NOT be shown again."
                : "Read-only access to product data (GET /api/fm/... endpoints)."}
            </DialogDescription>
          </DialogHeader>
          {newToken ? (
            <>
              <div
                className="break-all rounded-sm border border-amber-300 bg-amber-50 p-3 font-mono-data text-xs text-zinc-800"
                data-testid="new-api-token-value"
              >
                {newToken}
              </div>
              <DialogFooter>
                <Button
                  className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleCopy}
                  data-testid="copy-api-token-button"
                >
                  <Copy size={14} className="mr-1.5" /> Copy Token
                </Button>
                <Button variant="outline" className="rounded-sm border-zinc-300" onClick={() => closeCreate(false)}>
                  Done
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleCreate} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Token Name</Label>
                <Input
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  placeholder="e.g. ERP integration"
                  className="rounded-sm border-zinc-300"
                  data-testid="api-token-name-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={creating || !tokenName.trim()}
                  className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
                  data-testid="api-token-create-submit-button"
                >
                  {creating ? "Creating..." : "Create Token"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!revokeTarget} onOpenChange={(v) => !v && setRevokeTarget(null)}>
        <AlertDialogContent data-testid="revoke-api-token-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Token?</AlertDialogTitle>
            <AlertDialogDescription>
              "{revokeTarget?.name}" will stop working immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-red-600 text-white hover:bg-red-700"
              onClick={handleRevoke}
              data-testid="confirm-revoke-api-token-button"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
